import {
    Autocomplete,
    Chip,
    Grid,
    InputLabel,
    Radio,
    RadioGroup,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    useTheme,
} from "@mui/material";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import ApiAutocomplete from "@/common/ApiAutocomplete";
import {
    ACCOUNT_TYPES,
    getFieldsSet,
    VENDOR_PAYMENT_METHODS,
    TAX_FORM_TYPES,
    TAX_ID_TYPES,
} from "./constants";
import { CustomToggleButton, FormSection } from "./StyledComponents";
import { currencies } from "@/constants";
import { Heading } from "@/common/typography";
import { MaskedTextField } from "@/common/MaskedTextField";
import { request } from "@/Api";
import { useEffect } from "react";

export default function VendorForm({
    vendor,
    setVendor,
    children1,
    children2,
    children3,
    showBankingInfo,
    setErrors,
    errors = {},
    isWildcard,
}) {
    const isUpdating = vendor.id != null;
    const vendor_name = vendor.business_type
        ? vendor.business_type == "Individual"
            ? [vendor.first_name, vendor.last_name].join(" ").trim() || "Name"
            : vendor.company_name || "Company Name"
        : "Choose a Business Type";
    const vendor_name_value =
        vendor.business_type == "Individual"
            ? [vendor.first_name, vendor.last_name].join(" ").trim() || ""
            : vendor.company_name || "";
    const fieldsSet = getFieldsSet(
        vendor.payment_method,
        vendor.legal_entity?.currency,
    );

    const getDefaultLegalEntity = () => {
        request.get("/legal-entities/default").then((response) => {
            if (!isUpdating) {
                setVendor({
                    ...vendor,
                    legal_entity: response.data,
                    legal_entity_id: response.data.id,
                });
            }
        });
    };

    const handleInputChange = (field) => (event) => {
        const val = event.target.value;
        setVendor({ ...vendor, [field]: val, name: vendor_name_value });
        setErrors({ ...errors, [field]: null });
    };
    const handleAutocompleteChange = (field) => (event, val) => {
        const updatedVendor = { ...vendor };
        const updatedErrors = { ...errors };
        if (field == "city") {
            updatedVendor.city_id = val ? val.id : null;
        } else if (field == "legal_entity") {
            updatedVendor.legal_entity_id = val ? val.id : null;
            updatedErrors.legal_entity_id = null;
        } else if (field == "phone_number_country") {
            updatedVendor.phone_number_country_id = val ? val.id : null;
        }
        setVendor({ ...updatedVendor, [field]: val });
        setErrors({ ...updatedErrors, [field]: null });
    };

    const handleToggleButtonChange = (field) => (event, val) => {
        if (val == null) {
            return;
        }
        setVendor({ ...vendor, [field]: val });
        setErrors({ ...errors, [field]: null });
    };

    useEffect(() => {
        getDefaultLegalEntity();
    }, []);

    return (
        <>
            {!isWildcard ? (
                <TextField
                    disabled
                    value={vendor_name || ""}
                    onChange={handleInputChange("name")}
                    placeholder="Vendor Name"
                    variant="standard"
                    InputProps={{
                        style: { fontSize: "36px" },
                        disableUnderline: true,
                    }}
                    InputLabelProps={{ style: { fontSize: "36px" } }}
                    fullWidth
                />
            ) : (
                <>
                    <h1 style={{ fontWeight: 100 }}>
                        {isUpdating
                            ? "Update Your Vendor Profile"
                            : "Create Your Vendor Profile"}
                    </h1>
                </>
            )}

            {children1}

            <FormSection title={"Business Type"}>
                <ToggleButtonGroup
                    style={{ width: "100%" }}
                    orientation="horizontal"
                    value={vendor.business_type}
                    exclusive
                    size="small"
                    onChange={handleToggleButtonChange("business_type")}
                    color="primary"
                >
                    <CustomToggleButton
                        checked={vendor.business_type == "Individual"}
                        value="Individual"
                        icon={<PersonRoundedIcon />}
                    />
                    <CustomToggleButton
                        checked={vendor.business_type == "Company"}
                        value="Company"
                        icon={<ApartmentRoundedIcon />}
                    />
                </ToggleButtonGroup>
            </FormSection>

            <div hidden={!vendor.business_type}>
                <FormSection title={"Primary Contact"}>
                    <InputLabel>First Name</InputLabel>
                    <TextField
                        sx={{ marginBottom: 2 }}
                        fullWidth
                        size="small"
                        onChange={handleInputChange("first_name")}
                        value={vendor.first_name || ""}
                        error={errors.__root__ != null}
                        helperText={errors.__root__}
                    />
                    <InputLabel>Middle Name</InputLabel>
                    <TextField
                        sx={{ marginBottom: 2 }}
                        fullWidth
                        size="small"
                        onChange={handleInputChange("middle_name")}
                        value={vendor.middle_name || ""}
                    />
                    <InputLabel>Last Name</InputLabel>
                    <TextField
                        sx={{ marginBottom: 2 }}
                        fullWidth
                        size="small"
                        onChange={handleInputChange("last_name")}
                        value={vendor.last_name || ""}
                        error={errors.__root__ != null}
                        helperText={errors.__root__}
                    />
                    {vendor.business_type == "Company" && (
                        <>
                            <InputLabel>Company Name</InputLabel>
                            <TextField
                                sx={{ marginBottom: 2 }}
                                fullWidth
                                size="small"
                                onChange={handleInputChange("company_name")}
                                value={vendor.company_name || ""}
                            />
                        </>
                    )}
                    <ConditionallyRequiredInputLabel label={"Email"} required />
                    <TextField
                        disabled={isUpdating}
                        sx={{ marginBottom: 2 }}
                        fullWidth
                        size="small"
                        onChange={handleInputChange("email")}
                        value={vendor.email || ""}
                        error={errors.email != null}
                        helperText={errors.email}
                    />
                    <InputLabel>Phone Number</InputLabel>
                    <Grid container spacing={1}>
                        <Grid item>
                            <ApiAutocomplete
                                size="small"
                                sx={{ paddingBottom: 2, width: 200 }}
                                fullOptionData
                                value={vendor.phone_number_country || null}
                                apiRoute={
                                    "locations/dialing-code-supported-countries"
                                }
                                fields={["phone", "name"]}
                                onChange={handleAutocompleteChange(
                                    "phone_number_country",
                                )}
                                placeholder="Dialing Code"
                                InputProps={{
                                    startAdornment: (
                                        <span
                                            style={{
                                                paddingLeft: 8,
                                                paddingRight: 8,
                                            }}
                                        >
                                            +
                                        </span>
                                    ),
                                }}
                                error={errors.phone_number_country_id != null}
                                helperText={errors.phone_number_country_id}
                            />
                        </Grid>
                        <Grid item flexGrow={1}>
                            <TextField
                                sx={{ marginBottom: 2 }}
                                fullWidth
                                size="small"
                                onChange={handleInputChange("phone_number")}
                                value={vendor.phone_number || ""}
                                error={errors.phone_number != null}
                                helperText={errors.phone_number}
                            />
                        </Grid>
                    </Grid>
                    <InputLabel>Website</InputLabel>
                    <TextField
                        sx={{ marginBottom: 2 }}
                        fullWidth
                        size="small"
                        onChange={handleInputChange("website_url")}
                        value={vendor.website_url || ""}
                        error={errors.website_url != null}
                        helperText={errors.website_url}
                    />
                </FormSection>
                {children2}

                <FormSection title={"Business Address"}>
                    <InputLabel>Street Address 1</InputLabel>
                    <TextField
                        sx={{ marginBottom: 2 }}
                        fullWidth
                        size="small"
                        onChange={handleInputChange("street_address")}
                        value={vendor.street_address || ""}
                    />
                    <InputLabel>Street Address 2</InputLabel>
                    <TextField
                        sx={{ marginBottom: 2 }}
                        fullWidth
                        size="small"
                        onChange={handleInputChange("street_address_2")}
                        value={vendor.street_address_2 || ""}
                    />
                    <InputLabel>Postal Code</InputLabel>
                    <TextField
                        sx={{ marginBottom: 2 }}
                        fullWidth
                        size="small"
                        onChange={handleInputChange("postal_code")}
                        value={vendor.postal_code || ""}
                        error={errors.postal_code != null}
                        helperText={errors.postal_code}
                    />
                    <InputLabel>Location</InputLabel>
                    <ApiAutocomplete
                        size="small"
                        sx={{ paddingBottom: 2 }}
                        fullOptionData
                        value={vendor.city || null}
                        apiRoute={"locations"}
                        fields={["full_name"]}
                        onChange={handleAutocompleteChange("city")}
                        onInputResponseLimit={30}
                    />
                </FormSection>

                {showBankingInfo && (
                    <FormSection title={"Payment"}>
                        <ConditionallyRequiredInputLabel
                            label={"Payment Method"}
                            required={isWildcard}
                        />
                        <Autocomplete
                            size="small"
                            value={vendor.payment_method || null}
                            options={VENDOR_PAYMENT_METHODS}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    sx={{ marginBottom: 2 }}
                                />
                            )}
                            onChange={handleAutocompleteChange(
                                "payment_method",
                            )}
                        />
                        <ConditionallyRequiredInputLabel
                            label={"Legal Entity"}
                            required={isWildcard}
                        />
                        <ApiAutocomplete
                            size="small"
                            sx={{ paddingBottom: 2 }}
                            fullOptionData
                            value={vendor.legal_entity || null}
                            apiRoute={"legal-entities/public"}
                            fields={["name"]}
                            onChange={handleAutocompleteChange("legal_entity")}
                            disabled={vendor.external_id}
                            error={errors.legal_entity_id != null}
                            helperText={errors.legal_entity_id}
                        />
                        <ConditionallyRequiredInputLabel
                            label={"Payment Currency"}
                            required={isWildcard}
                        />
                        <Autocomplete
                            size="small"
                            value={vendor.payment_currency || null}
                            options={currencies}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    sx={{ marginBottom: 2 }}
                                    error={errors.payment_currency != null}
                                    helperText={errors.payment_currency}
                                />
                            )}
                            onChange={handleAutocompleteChange(
                                "payment_currency",
                            )}
                        />
                        <div hidden={!fieldsSet.includes("account_type")}>
                            <InputLabel>Account Type</InputLabel>
                            <Autocomplete
                                size="small"
                                value={vendor.account_type || null}
                                options={ACCOUNT_TYPES}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        sx={{ marginBottom: 2 }}
                                        error={errors.account_type != null}
                                        helperText={errors.account_type}
                                    />
                                )}
                                onChange={handleAutocompleteChange(
                                    "account_type",
                                )}
                            />
                        </div>
                        <ConditionallyRequiredInputLabel
                            label={"Name on Account"}
                            required={isWildcard}
                        />
                        <TextField
                            sx={{ marginBottom: 2 }}
                            fullWidth
                            size="small"
                            onChange={handleInputChange("name_on_account")}
                            value={vendor.name_on_account || ""}
                            error={errors.name_on_account != null}
                            helperText={errors.name_on_account}
                        />
                        <div>
                            <InputLabel>Bank Address</InputLabel>
                            <TextField
                                sx={{ marginBottom: 2 }}
                                fullWidth
                                size="small"
                                onChange={handleInputChange("bank_address")}
                                value={vendor.bank_address || ""}
                            />
                        </div>
                        <InputLabel>Bank Address 2</InputLabel>
                        <TextField
                            sx={{ marginBottom: 2 }}
                            fullWidth
                            size="small"
                            onChange={handleInputChange("bank_address_2")}
                            value={vendor.bank_address_2 || ""}
                        />
                        <div>
                            <ConditionallyRequiredInputLabel
                                label={"Bank Name"}
                                required={isWildcard}
                            />
                            <TextField
                                sx={{ marginBottom: 2 }}
                                fullWidth
                                size="small"
                                onChange={handleInputChange("bank_name")}
                                value={vendor.bank_name || ""}
                                error={errors.bank_name != null}
                                helperText={errors.bank_name}
                            />
                        </div>
                        <div hidden={!fieldsSet.includes("swift_code")}>
                            <ConditionallyRequiredInputLabel
                                label={"SWIFT"}
                                required={isWildcard}
                            />
                            <TextField
                                sx={{ marginBottom: 2 }}
                                fullWidth
                                size="small"
                                onChange={handleInputChange("swift_code")}
                                value={vendor.swift_code || ""}
                                error={errors.swift_code != null}
                                helperText={errors.swift_code}
                            />
                        </div>
                        <div hidden={!fieldsSet.includes("institution_number")}>
                            <ConditionallyRequiredInputLabel
                                label={"Institution Number"}
                                required={isWildcard}
                            />
                            <TextField
                                sx={{ marginBottom: 2 }}
                                fullWidth
                                size="small"
                                onChange={handleInputChange(
                                    "institution_number",
                                )}
                                value={vendor.institution_number || ""}
                                error={errors.institution_number != null}
                                helperText={errors.institution_number}
                            />
                        </div>
                        <div
                            hidden={
                                !fieldsSet.includes("branch_routing_number")
                            }
                        >
                            <ConditionallyRequiredInputLabel
                                label={"Branch Routing Number"}
                                required={isWildcard}
                            />
                            <TextField
                                sx={{ marginBottom: 2 }}
                                fullWidth
                                size="small"
                                onChange={handleInputChange(
                                    "branch_routing_number",
                                )}
                                value={vendor.branch_routing_number || ""}
                                error={errors.branch_routing_number != null}
                                helperText={errors.branch_routing_number}
                            />
                        </div>
                        <div hidden={!fieldsSet.includes("aba_number")}>
                            <ConditionallyRequiredInputLabel
                                label={"ABA Number"}
                                required={isWildcard}
                            />
                            <TextField
                                sx={{ marginBottom: 2 }}
                                fullWidth
                                size="small"
                                onChange={handleInputChange("aba_number")}
                                value={vendor.aba_number || ""}
                                error={errors.aba_number != null}
                                helperText={errors.aba_number}
                            />
                        </div>
                        <ConditionallyRequiredInputLabel
                            label={"Account Number"}
                            required={isWildcard}
                        />
                        <MaskedTextField
                            sx={{ marginBottom: 2 }}
                            fullWidth
                            size="small"
                            onChange={handleInputChange("account_number")}
                            placeholder={vendor.account_number_masked}
                            error={errors.account_number != null}
                            helperText={errors.account_number}
                        />
                    </FormSection>
                )}

                <FormSection title={"Tax Information"}>
                    <ConditionallyRequiredInputLabel label={"Tax Form Id"} />
                    <MaskedTextField
                        sx={{ marginBottom: 2 }}
                        fullWidth
                        size="small"
                        onChange={handleInputChange("tax_id")}
                        error={errors.tax_id != null}
                        helperText={errors.tax_id}
                        placeholder={vendor.tax_id_masked}
                    />
                    <ConditionallyRequiredInputLabel label={"Tax Id Type"} />
                    <Autocomplete
                        freeSolo
                        size="small"
                        value={vendor.tax_id_type || null}
                        options={TAX_ID_TYPES[vendor.payment_currency] || []}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                sx={{ marginBottom: 2 }}
                                error={errors.tax_id_type != null}
                                helperText={errors.tax_id_type}
                            />
                        )}
                        onChange={handleAutocompleteChange("tax_id_type")}
                        onInput={handleInputChange("tax_id_type")}
                    />
                    <ConditionallyRequiredInputLabel label={"Tax Form Type"} />
                    <Autocomplete
                        freeSolo
                        size="small"
                        value={vendor.tax_form_type || null}
                        options={TAX_FORM_TYPES[vendor.payment_currency] || []}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                sx={{ marginBottom: 2 }}
                                error={errors.tax_form_type != null}
                                helperText={errors.tax_form_type}
                            />
                        )}
                        onChange={handleAutocompleteChange("tax_form_type")}
                        onInput={handleInputChange("tax_form_type")}
                    />
                </FormSection>

                {children3}
            </div>
        </>
    );
}

const ConditionallyRequiredInputLabel = ({ label, required }) => {
    const theme = useTheme();
    return (
        <InputLabel style={{ fontSize: theme.typography.body1.fontSize }}>
            {label}
            {required && (
                <span style={{ color: theme.palette.error.main }}>*</span>
            )}
        </InputLabel>
    );
};
